import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, Button, TextField, MenuItem, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import BusinessCardContainer from './BusinessCardContainer';
import AppSearchBar from '../../../Components/AppSearchBar';
import routes from '../../../Utils/routes';
import nodata from "../../../Assets/Mentorship.svg"

interface LocationState {
    businesses: any[];
    title: string;
    searchInput?: string;
    selectedFilterType?: string;
    selectedLocation?: string; 
}

const AllBusinesses: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { businesses, title, searchInput: initialSearch, selectedFilterType: initialType, selectedLocation: initialLocation } = location.state as LocationState;

    const [searchInput, setSearchInput] = useState(initialSearch || '');
    const [selectedFilterType, setSelectedFilterType] = useState(initialType || '');
    const [selectedLocation, setSelectedLocation] = useState(initialLocation || '');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 600);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const filtered = businesses.filter(business => {
            const matchesSearch = searchInput.trim() === "" ||
                business.Name.toLowerCase().includes(searchInput.trim().toLowerCase());

            const matchesType = selectedFilterType === "" ||
                business.Business_Type__c === selectedFilterType;

            const matchesLocation = selectedLocation === "" ||
                (business.Business_Address__c &&
                    business.Business_Address__c.toLowerCase().includes(selectedLocation.toLowerCase()));

            return matchesSearch && matchesType && matchesLocation;
        });

        setFilteredBusinesses(filtered);
    }, [businesses, searchInput, selectedFilterType, selectedLocation]);

    const handleBack = () => {
        navigate(routes.businessdirectory);
    };

    const handleBusinessClick = (businessId: string) => {
        const selectedBusiness = businesses.find(business => business.Id === businessId);
        if (selectedBusiness) {
            navigate(`/business directory`, {
                state: {
                    businessName: selectedBusiness.Name,
                    businessSource: 'all',
                    selectedBusinessData: selectedBusiness,
                    selectedBusinessId: businessId,
                    view: 'detail'
                }
            });
        }
    };

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const closeFilter = () => {
        setIsFilterOpen(false);
    };

    const clearFilters = () => {
        setSelectedFilterType('');
        setSelectedLocation('');
    };

    const applyFilters = () => {
        setIsFilterOpen(false);
    };

    const distinctTypes = Array.from(
        new Set(businesses.map(business => business.Business_Type__c).filter(Boolean))
    ) as string[];

    const distinctLocations = Array.from(
        new Set(businesses.map(business => business.Business_Address__c).filter(Boolean))
    ) as string[];

    const transformedBusinessCards = filteredBusinesses.map(business => {
        const defaultContact = {
            name: "Unknown Contact",
            image: "https://thumbs.dreamstime.com/b/creative-illustration-default-avatar-profile-placeholder-isolated-background-art-design-grey-photo-blank-template-mockup-144855718.jpg"
        };

        let contactInfo = defaultContact;
        if (business.Business_Directories__r && business.Business_Directories__r.records.length > 0) {
            const firstContact = business.Business_Directories__r.records[0].Contact__r;
            contactInfo = {
                name: firstContact.Name || defaultContact.name,
                image: firstContact.Profile_Picture_URL__c || defaultContact.image
            };
        }

        return {
            id: business.Id,
            businessImage: business.Cover_Image_Url__c || "https://ample.co.in/wp-content/uploads/2023/12/img-main.jpg",
            logo: business.Logo_URL__c || "https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg",
            category: business.Business_Type__c || "No Category",
            name: business.Name,
            location: business.Business_Address__c || "No Location",
            contactName: contactInfo.name,
            contactImage: contactInfo.image
        };
    });

    return (
        <Box sx={{
            p: { xs: 1, sm: 2 },
            minHeight: { sm: '83.5vh' },
            backgroundColor: 'white',
            borderRadius: '12px'
        }}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" mb={3}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{
                                color: "#373A45",
                                fontWeight: 500,
                                mr: 2
                            }}
                        >
                            {title}
                        </Button>
                    </Box>

                    <Grid container spacing={2} alignItems="center" mb={3}>
                        <Grid item xs={8} md={8}>
                            <AppSearchBar
                                localSearchInput={searchInput}
                                handleChange={(e) => setSearchInput(e.target.value)}
                                handleMicHold={() => { }}
                                handleMicRelease={() => { }}
                                openSnackbar={false}
                            />
                        </Grid>
                        <Grid item xs={4} md={4} sx={{ position: 'relative' }}>
                            <Button
                                fullWidth
                                style={{
                                    color: "#373A45",
                                    backgroundColor: "#E8EBF3",
                                    borderRadius: 5,
                                    padding: "7px 13px"
                                }}
                                startIcon={<FilterAltOutlinedIcon style={{ color: "#373A45" }} />}
                                onClick={toggleFilter}
                            >
                                Filter
                            </Button>

                            {/* Filter Modal */}
                            {isFilterOpen && (
                                <Paper
                                    style={
                                        isMobile
                                            ? {
                                                position: "absolute",
                                                right: "0",
                                                top: "calc(100% + 10px)",
                                                backgroundColor: "white",
                                                width: "21rem", // Fixed width for mobile
                                                zIndex: "999",
                                                border: "1px solid #E4E4E7",
                                                borderRadius: "12px",
                                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                overflowY: "auto",
                                                maxHeight: "400px",
                                                minHeight: '340px',
                                                padding: 0,
                                                paddingBottom: '80px',
                                            }
                                            : {
                                                position: "absolute",
                                                right: "0",
                                                top: "calc(100% + 10px)",
                                                backgroundColor: "white",
                                                width: "360px",
                                                zIndex: "999",
                                                border: "1px solid #E4E4E7",
                                                borderRadius: "12px",
                                                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                                                overflowY: "auto",
                                                maxHeight: "400px",
                                                minHeight: '340px',
                                                padding: 0,
                                                paddingBottom: '80px',
                                            }
                                    }
                                >
                                    <Box p={3} pb={2}>
                                        <Grid container>
                                            <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                                                <Typography variant="h6" style={{ fontWeight: "600", fontSize: "18px" }}>
                                                    Filters
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                                                <IconButton onClick={closeFilter} size="small">
                                                    <CloseIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Box px={3} pb={3}>
                                        <Box mb={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                placeholder="Location"
                                                value={selectedLocation}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                variant="outlined"
                                                SelectProps={{
                                                    displayEmpty: true,
                                                    renderValue: (selected) => {
                                                        return selected ? String(selected) : "Location";
                                                    }
                                                }}
                                                InputProps={{
                                                    style: {
                                                        height: "36px",
                                                        border: "0.5px solid #B2B3B7",
                                                        borderRadius: "8px"
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                            borderRadius: '8px',
                                                        },
                                                        '&:hover fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        height: '36px',
                                                        paddingTop: '0',
                                                        paddingBottom: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>All Locations</em>
                                                </MenuItem>
                                                {distinctLocations.map((location) => (
                                                    <MenuItem key={location} value={location}>
                                                        {location}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        <Box mb={2}>
                                            <TextField
                                                select
                                                fullWidth
                                                placeholder="Business type"
                                                value={selectedFilterType}
                                                onChange={(e) => setSelectedFilterType(e.target.value)}
                                                variant="outlined"
                                                SelectProps={{
                                                    displayEmpty: true,
                                                    renderValue: (selected) => {
                                                        return selected ? String(selected) : "Business type";
                                                    }
                                                }}
                                                InputProps={{
                                                    style: {
                                                        height: "36px",
                                                        border: "0.5px solid #B2B3B7",
                                                        borderRadius: "8px"
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                            borderRadius: '8px',
                                                        },
                                                        '&:hover fieldset': {
                                                            border: '0.5px solid #B2B3B7',
                                                        },
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        height: '36px',
                                                        paddingTop: '0',
                                                        paddingBottom: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>All Business Types</em>
                                                </MenuItem>
                                                {distinctTypes.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>

                                        <Box
                                            mt={8}
                                            display="flex"
                                            justifyContent="space-between"
                                            style={{
                                                position: "absolute",
                                                bottom: "20px",
                                                left: "0",
                                                right: "0",
                                                padding: "0 24px"
                                            }}
                                        >
                                            <Button
                                                onClick={clearFilters}
                                                style={{
                                                    borderRadius: "8px",
                                                    color: "#111827",
                                                    backgroundColor: "white",
                                                    // padding: "8px 16px",
                                                    width: "45%",
                                                    textTransform: "none",
                                                    fontWeight: "500",
                                                    border: "1px solid #E4E4E7"
                                                }}
                                            >
                                                Reset all filters
                                            </Button>
                                            <Button
                                                onClick={applyFilters}
                                                style={{
                                                    background: "#2C60AA",
                                                    borderRadius: "8px",
                                                    color: "#fff",
                                                    // padding: "8px 16px",
                                                    width: "45%",
                                                    textTransform: "none",
                                                    fontWeight: "500"
                                                }}
                                                sx={{
    "&:hover": {
      background: "#2C60AA",
      color: "#fff",
    },
  }}
                                            >
                                                Show results
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1, sm: 2 },
                            borderRadius: { xs: '9px', sm: '8px' },
                            backgroundColor: 'white',
                            height: { sm: 'auto' }
                        }}
                    >
                        {transformedBusinessCards.length === 0 ? (
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
                                <img src={nodata} alt="No results" width={120} style={{ marginBottom: 16 }} />
                                <Typography variant="h5" color="textSecondary">
                                    No businesses found matching your filters.
                                </Typography>
                            </Box>
                        ) : (
                            <BusinessCardContainer
                                cardData={transformedBusinessCards}
                                onCardClick={handleBusinessClick}
                            />
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AllBusinesses; 